/**
 * The project's Data tab (vfs-service src/data, in the person's sandbox pod): every datastore the project's services
 * use, browsable and editable — tables (PostgreSQL, SQLite), documents (MongoDB), keys (Redis), buckets (S3), the graph
 * (Neo4j), topics (Kafka) — and a query box for each. Every call goes through vfs-service's base URL with the person's
 * authorized fetch, as CollabService does. GETs need a viewer; writes an editor (the server checks).
 *
 * Store: `{ id, kind, name, status: 'running'|'stopped'|'empty', readOnly, services, sizeBytes?, objects: [{ name, kind,
 * count?, schema? }] }`; `id` is the kind (one store of each kind per project).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const dataUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/data${path}`;
const seg = (value) => encodeURIComponent(String(value));

/** An HTTP error with the status and the JSON body vfs-service answered (`{ error, code? }`). */
export class DataError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'DataError';
    this.status = status;
    this.body = body;
    this.code = body?.code || null;
  }
}

async function send(url, { method = 'GET', body, raw, contentType, accept = 'application/json' } = {}) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  return getAuthorizedFetch()(url, {
    method,
    headers: {
      Accept: accept,
      ...buildAclContextHeaders(),
      ...(json ? { 'Content-Type': 'application/json' } : {}),
      ...(raw !== undefined ? { 'Content-Type': contentType || 'application/octet-stream' } : {}),
    },
    ...(json ? { body: JSON.stringify(body) } : {}),
    ...(raw !== undefined ? { body: raw } : {}),
  });
}

// A 404 that is not the API's own JSON answer: the route does not exist, i.e. the sandbox still runs a vfs-service from
// before this part of the Data tab (it updates on its next wake, or with the sandbox admin's Update).
export const SANDBOX_OUTDATED = 'Your sandbox runs an older version that does not have this yet. Put it to sleep from the Sandbox page and open the project again (or ask your sandbox admin to Update it).';

/** The error for a response that is not ok (shared with the Traces tab's calls, services/TracesService.js). */
export async function fail(response) {
  const data = await response.json().catch(() => null);
  if (response.status === 404 && !data) {
    return new DataError(SANDBOX_OUTDATED, 404, { code: 'sandbox_outdated' });
  }
  return new DataError(data?.error || data?.message || `Request failed (${response.status})`, response.status, data);
}

async function call(url, options) {
  const response = await send(url, options);
  if (!response.ok) { throw await fail(response); }
  if (response.status === 204) { return {}; }
  return (await response.json().catch(() => null)) || {};
}

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') { search.set(key, typeof value === 'object' ? JSON.stringify(value) : String(value)); }
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

// ---- Overview ----

/** `{ stores: [Store] }`. */
export function getOverview(projectId) {
  return call(dataUrl(projectId, ''));
}

// ---- Add a store ----

/**
 * `POST /scaffold/store` (editors): writes the declaration file of `kind` into each of `services` (default: every service
 * of the project). `dryRun`: only what it would write. `{ kind, dryRun, services: [{ service, files, written, skipped,
 * already }], written }`; `already`: the service declares the kind, nothing to write.
 */
export function addStore(projectId, { kind, services, dryRun = false }) {
  return call(`${base()}/api/v1/projects/${seg(projectId)}/scaffold/store`, { method: 'POST', body: { kind, ...(services ? { services } : {}), dryRun } });
}

// ---- Tables (postgres, sqlite) ----

/**
 * `{ columns: [{ name, type, nullable, default, pk, fk? }], primaryKey, rows, total }`. `filter`: `[{ column, op, value }]`
 * (AND), sent as JSON; `dir`: 'asc' | 'desc'.
 */
export function getTableRows(projectId, store, table, { schema, limit = 50, offset = 0, sort, dir, filter } = {}) {
  return call(dataUrl(projectId, `/${seg(store)}/tables/${seg(table)}${query({
    schema, limit, offset, sort, dir: sort ? dir : undefined, filter: filter?.length ? filter : undefined,
  })}`));
}

/** The statements the staged changes would run: `{ statements: [{ op, sql, params, note? }], effects: [text] }`. */
export function previewChanges(projectId, store, changes) {
  return call(dataUrl(projectId, `/${seg(store)}/changes`), { method: 'POST', body: { changes, apply: false } });
}

/**
 * Runs the staged changes in one transaction: `{ applied, changeId, undoUntil }`; a DataError 409/400 when one fails.
 * `summary`: the person's words for Activity (else the server's own "updated 1 row in products").
 */
export function applyChanges(projectId, store, changes, { summary } = {}) {
  return call(dataUrl(projectId, `/${seg(store)}/changes`), { method: 'POST', body: { changes, apply: true, ...(summary ? { summary } : {}) } });
}

/**
 * Which staged changes would fail, and why, without keeping anything (each in a savepoint, all rolled back; editors):
 * `{ checked, errors: [{ index, error, sqlState? }], more? }`.
 */
export function checkChanges(projectId, store, changes) {
  return call(dataUrl(projectId, `/${seg(store)}/changes`), { method: 'POST', body: { changes, check: true } });
}

/** Puts back what a change (rows or a document) replaced, within 10 minutes: `{ undone: true }`. */
export function undoChange(projectId, changeId) {
  return call(dataUrl(projectId, `/undo/${seg(changeId)}`), { method: 'POST', body: {} });
}

// ---- Documents (mongodb) ----

/** `{ docs, total }`. `filter`, `sort`: Extended JSON text (or objects). */
export function listDocs(projectId, collection, { filter, sort, limit = 20, skip = 0 } = {}) {
  return call(dataUrl(projectId, `/mongodb/collections/${seg(collection)}/docs${query({ filter, sort, limit, skip })}`));
}

/** The URL id of a document: the Extended JSON of its `_id`. */
export const docIdOf = (id) => JSON.stringify(id ?? null);

export function insertDoc(projectId, collection, doc) {
  return call(dataUrl(projectId, `/mongodb/collections/${seg(collection)}/docs`), { method: 'POST', body: { doc } });
}

/** Replaces the document: `{ ok, changeId }`. `id`: its `_id` (as the list gave it). */
export function replaceDoc(projectId, collection, id, doc) {
  return call(dataUrl(projectId, `/mongodb/collections/${seg(collection)}/docs/${seg(docIdOf(id))}`), { method: 'PUT', body: { doc } });
}

/**
 * "Import JSON": inserts the documents (Extended JSON) in one call, those that fail do not stop the others:
 * `{ inserted, failed: [{ index, error }], failedCount, changeId?, undoUntil? }`.
 */
export function importDocs(projectId, collection, docs, { summary } = {}) {
  return call(dataUrl(projectId, `/mongodb/collections/${seg(collection)}/import`), { method: 'POST', body: { docs, ...(summary ? { summary } : {}) } });
}

export function deleteDoc(projectId, collection, id) {
  return call(dataUrl(projectId, `/mongodb/collections/${seg(collection)}/docs/${seg(docIdOf(id))}`), { method: 'DELETE' });
}

// ---- Keys (redis) ----

/** `{ keys: [{ key, type, ttl }], cursor }` (keys without the project's prefix; cursor '0': the end). */
export function listRedisKeys(projectId, { match = '*', cursor = '0', count = 100 } = {}) {
  return call(dataUrl(projectId, `/redis/keys${query({ match, cursor, count })}`));
}

/** `{ key, type, ttl, value }`. */
export function getRedisKey(projectId, key) {
  return call(dataUrl(projectId, `/redis/keys/${seg(key)}`));
}

export function setRedisKey(projectId, key, { type, value, ttl }) {
  return call(dataUrl(projectId, `/redis/keys/${seg(key)}`), {
    method: 'PUT', body: { type, value, ...(ttl !== undefined ? { ttl } : {}) },
  });
}

export function deleteRedisKey(projectId, key) {
  return call(dataUrl(projectId, `/redis/keys/${seg(key)}`), { method: 'DELETE' });
}

/** `ttl`: seconds, or null for no expiry. */
export function setRedisTtl(projectId, key, ttl) {
  return call(dataUrl(projectId, `/redis/keys/${seg(key)}/ttl`), { method: 'POST', body: { ttl } });
}

/**
 * The keys that may cache the changed rows: `targets: [{ name, ids }]` →
 * `{ keys: [{ key, type, ttl, reason: 'row' | 'object', target }], scanned, complete, more?, stopped? }`.
 */
export function redisHint(projectId, targets) {
  return call(dataUrl(projectId, '/redis/hint'), { method: 'POST', body: { targets } });
}

/** Deletes those keys (editors; one change with Undo): `{ removed, changeId?, undoUntil? }`. */
export function clearRedisKeys(projectId, keys, reason) {
  return call(dataUrl(projectId, '/redis/clear'), { method: 'POST', body: { keys, ...(reason ? { reason } : {}) } });
}

// ---- Buckets (s3) ----

/** `{ buckets: [{ name, logical, objects?, sizeBytes? }] }`. */
export function listBuckets(projectId) {
  return call(dataUrl(projectId, '/s3/buckets'));
}

/** `{ folders, objects: [{ key, size, lastModified, contentType? }], nextToken }`. */
export function listObjects(projectId, bucket, { prefix = '', delimiter = '/', token } = {}) {
  return call(dataUrl(projectId, `/s3/buckets/${seg(bucket)}/objects${query({ prefix, delimiter, token })}`));
}

/** The object's URL (through Flow, needs the person's token: fetch it with getObjectBlob). */
export function objectUrl(projectId, bucket, key, { download = false } = {}) {
  return dataUrl(projectId, `/s3/buckets/${seg(bucket)}/object${query({ key, download: download ? 1 : undefined })}`);
}

/** The object's bytes as a Blob (for a preview or a download). */
export async function getObjectBlob(projectId, bucket, key, { download = false } = {}) {
  const response = await send(objectUrl(projectId, bucket, key, { download }), { accept: '*/*' });
  if (!response.ok) { throw await fail(response); }
  return response.blob();
}

/**
 * A zip (a Blob) of a folder, of picked files and folders, or of everything under `base` (no `paths`): `base` is the
 * folder the names inside are relative to, a path ending in "/" is a folder.
 */
export async function getBucketZip(projectId, bucket, { base = '', paths = [] } = {}) {
  const response = await send(dataUrl(projectId, `/s3/buckets/${seg(bucket)}/zip`), { method: 'POST', body: { base, paths }, accept: 'application/zip' });
  if (!response.ok) { throw await fail(response); }
  return response.blob();
}

/** Uploads (or replaces) an object: `body` a File/Blob, sent as is with its type. */
export function putObject(projectId, bucket, key, body, contentType) {
  return call(dataUrl(projectId, `/s3/buckets/${seg(bucket)}/object${query({ key })}`), {
    method: 'PUT', raw: body, contentType: contentType || body?.type || 'application/octet-stream',
  });
}

export function deleteObject(projectId, bucket, key) {
  return call(dataUrl(projectId, `/s3/buckets/${seg(bucket)}/object${query({ key })}`), { method: 'DELETE' });
}

/** Renames or moves an object: `{ from, to }` keys. */
export function moveObject(projectId, bucket, from, to) {
  return call(dataUrl(projectId, `/s3/buckets/${seg(bucket)}/move`), { method: 'POST', body: { from, to } });
}

/** `{ url, expiresAt, note? }`: a link to the object. */
export function objectLink(projectId, bucket, key, expiresSeconds = 3600) {
  return call(dataUrl(projectId, `/s3/buckets/${seg(bucket)}/link`), { method: 'POST', body: { key, expiresSeconds } });
}

/**
 * The rows and documents that mention the object's key: `{ refs: [{ store: 'postgres', schema, table, column, key } |
 * { store: 'mongodb', collection, field, _id }], partial, cached }`.
 */
export function objectUsedIn(projectId, bucket, key) {
  return call(dataUrl(projectId, `/s3/buckets/${seg(bucket)}/used-in${query({ key })}`));
}

// ---- Graph (neo4j) ----

/** `{ labels: [{ label, count }], relationships: [{ type, count }] }`. */
export function getGraphLabels(projectId) {
  return call(dataUrl(projectId, '/neo4j/labels'));
}

// ---- Topics (kafka) ----
// A topic is named as the list gives it: `{ name, topic }`, `topic` being the real name. When it is there the calls
// send it with `raw=1` (so `<ident>.messages` and an unprefixed `messages` never mix); a bare string is the old name.

const topicRef = (topic) => (topic && typeof topic === 'object'
  ? (topic.topic ? { path: seg(topic.topic), raw: 1 } : { path: seg(topic.name), raw: undefined })
  : { path: seg(topic), raw: undefined });

/** `{ topics: [{ name, topic, partitions, messages (null when unknown), end, unprefixed?, lastMessageAt?, error? }] }`. */
export function listTopics(projectId, { withLast = false } = {}) {
  return call(dataUrl(projectId, `/kafka/topics${query({ withLast: withLast ? 1 : undefined })}`));
}

/**
 * `{ messages: [{ partition, offset, timestamp, key, value, headers, encoding?, consumedBy? }], partitions?, groups?, partial? }`,
 * oldest first: the latest `limit`, or with `from` the first `limit` from that offset.
 */
export function getTopicMessages(projectId, topic, { limit = 50, partition, from } = {}) {
  const ref = topicRef(topic);
  return call(dataUrl(projectId, `/kafka/topics/${ref.path}/messages${query({ limit, partition, from, raw: ref.raw })}`));
}

/** `{ partitions: [{ partition, leader, replicas, isr, low, high, messages }], config }`. */
export function getTopicPartitions(projectId, topic) {
  const ref = topicRef(topic);
  return call(dataUrl(projectId, `/kafka/topics/${ref.path}/partitions${query({ raw: ref.raw })}`));
}

/** `{ end, low, messages, lag, groups: [{ groupId, state, members, partitions, lag, committed, end, lastConsumed? }] }`. */
export function getTopicConsumers(projectId, topic, { last = true } = {}) {
  const ref = topicRef(topic);
  return call(dataUrl(projectId, `/kafka/topics/${ref.path}/consumers${query({ raw: ref.raw, last: last ? undefined : 0 })}`));
}

/** `{ groups: [{ groupId, state, members, lag, topics: [{ name, topic, partitions, lag, committed, end }] }] }`. */
export function listConsumerGroups(projectId) {
  return call(dataUrl(projectId, '/kafka/groups'));
}

/** Moves an empty group's committed offsets on `topic` to `to` ('earliest' | 'latest' | 'offset' with `offset`). */
export function resetConsumerGroup(projectId, groupId, { topic, to, offset, partition }) {
  const ref = topicRef(topic);
  const name = topic && typeof topic === 'object' ? (topic.topic || topic.name) : topic;
  return call(dataUrl(projectId, `/kafka/groups/${seg(groupId)}/reset`), {
    method: 'POST',
    body: { topic: name, raw: Boolean(ref.raw), to, ...(to === 'offset' ? { offset: Number(offset) } : {}), ...(partition === undefined || partition === '' ? {} : { partition: Number(partition) }) },
  });
}

export function deleteConsumerGroup(projectId, groupId) {
  return call(dataUrl(projectId, `/kafka/groups/${seg(groupId)}`), { method: 'DELETE' });
}

export function createTopic(projectId, { name, partitions = 1 }) {
  return call(dataUrl(projectId, '/kafka/topics'), { method: 'POST', body: { name, partitions } });
}

export function deleteTopic(projectId, topic) {
  const ref = topicRef(topic);
  return call(dataUrl(projectId, `/kafka/topics/${ref.path}${query({ raw: ref.raw })}`), { method: 'DELETE' });
}

export function produceMessage(projectId, topic, { key, value, headers }) {
  const ref = topicRef(topic);
  return call(dataUrl(projectId, `/kafka/topics/${ref.path}/messages${query({ raw: ref.raw })}`), {
    method: 'POST', body: { value, ...(key ? { key } : {}), ...(headers && Object.keys(headers).length ? { headers } : {}) },
  });
}

// ---- Query ----

/** `{ columns, rows, rowCount, ms, truncated, wrote }`. Read-only unless `allowWrite` (an editor's confirmed write). */
export function runQuery(projectId, { store, language, query: text, allowWrite = false }) {
  return call(dataUrl(projectId, '/query'), { method: 'POST', body: { store, language, query: text, allowWrite: Boolean(allowWrite) } });
}

/** `{ query, explanation }` from words; a DataError 501 (`code: 'not_available'`) where there is no model for it. */
export function askQuery(projectId, { store, language, prompt }) {
  return call(dataUrl(projectId, '/ask'), { method: 'POST', body: { store, language, prompt } });
}

/**
 * The plan of a query, nothing changed: `{ store, language, engine, plan: Node, notes, totals, analyzed, ms }` with
 * Node = `{ op, target?, facts: [[label, value]], warn?, children }`. `analyze` (PostgreSQL only): runs it read-only to measure.
 */
export function explainQuery(projectId, { store, language, query: text, analyze = false }) {
  return call(dataUrl(projectId, '/explain'), { method: 'POST', body: { store, language, query: text, ...(analyze ? { analyze: true } : {}) } });
}

// ---- Snippets (saved queries, shared in the project) ----

/** `{ snippets: [{ id, name, language, store?, query, createdAt, updatedAt, by? }] }`. */
export function listSnippets(projectId) {
  return call(dataUrl(projectId, '/snippets'));
}

export function saveSnippet(projectId, { name, language, store, query: text }) {
  return call(dataUrl(projectId, '/snippets'), { method: 'POST', body: { name, language, store, query: text } });
}

/** Renames (or changes the query of) a snippet: `{ snippet }`. */
export function updateSnippet(projectId, id, changes) {
  return call(dataUrl(projectId, `/snippets/${seg(id)}`), { method: 'PUT', body: changes });
}

export function deleteSnippet(projectId, id) {
  return call(dataUrl(projectId, `/snippets/${seg(id)}`), { method: 'DELETE' });
}

// ---- Restore points (the project's data at an earlier point, Kafka topics included) ----
// Point: `{ id, projectId, kind: 'auto'|'manual'|'pre-change'|'backup', name, createdAt, sizeBytes, by, reason, source,
// stores: [{ store, sizeBytes, objects: [{ name, kind, count }], warnings }], skipped: [{ store, reason }], digest }`.

/**
 * `{ available, reason?: 'no-runner'|'old-runner'|'unavailable', note?, points: [Point] (newest first), budget: { usedBytes,
 * maxBytes, diskBytes, percent }, policy: { hourly, daily, preChange, budgetPercent, maxBytes } }`.
 */
export function listRestorePoints(projectId) {
  return call(dataUrl(projectId, '/restore-points'));
}

/** Saves a restore point now (editors): `{ point }` (kind manual). */
export function saveRestorePoint(projectId, name) {
  return call(dataUrl(projectId, '/restore-points'), { method: 'POST', body: { name } });
}

/** `{ point }`. */
export function getRestorePoint(projectId, id) {
  return call(dataUrl(projectId, `/restore-points/${seg(id)}`));
}

export function deleteRestorePoint(projectId, id) {
  return call(dataUrl(projectId, `/restore-points/${seg(id)}`), { method: 'DELETE' });
}

/**
 * Puts the stores back as they were in the point (editors): `{ restored: [{ store, ok, error? }], before: Point|null,
 * point }`. `stores`: the store ids (all of the point's when left out); `confirm`: the project's name as the person typed
 * it (a DataError 400 `confirm_mismatch` when it is not the name).
 */
export function restoreRestorePoint(projectId, id, { stores, confirm } = {}) {
  return call(dataUrl(projectId, `/restore-points/${seg(id)}/restore`), {
    method: 'POST', body: { ...(stores?.length ? { stores } : {}), confirm },
  });
}

/**
 * The nightly backups of the sandbox: `{ available, reason?, items: [{ id, createdAt, sizeBytes, stores: [id],
 * notRestorable: [{ store, reason }] }] }`.
 */
export function listBackups(projectId) {
  return call(dataUrl(projectId, '/backups'));
}

/** Turns a nightly backup into a restore point of this project (editors): `{ point }` (kind backup). */
export function importBackup(projectId, backupId) {
  return call(dataUrl(projectId, `/backups/${seg(backupId)}/import`), { method: 'POST', body: {} });
}
